/**
 * El contrato de `/api/v1/`, escrito a mano y a propósito.
 *
 * No es un tipo generado: es la lectura que este front hace del contrato del
 * backend (SPEC-009 §5). Cuando el backend añada un campo, esto no se entera —
 * y está bien, porque añadir es compatible. Cuando *quite* uno, las pruebas de
 * contrato del backend fallan allá, que es donde tienen que fallar.
 */

// ─────────────────────────── vocabulario ───────────────────────────

/** specs/000-dominio.md §3 */
export type EstadoMovimiento =
  | "CONCILIADO"
  | "IDENTIFICADO"
  | "POR_CONFIRMAR"
  | "CONFLICTO"
  | "SIN_IDENTIFICAR"
  | "DATO_INVALIDO";

export type EstadoHuerfano = "ESPERADO_SIN_BANCO" | "FALTANTE" | "PENDIENTE_ANTIGUEDAD" | "REVISADO";

/** specs/003-libro-decisiones.md §6 — de dónde viene la certeza. */
export type Origen =
  | "automatico"
  | "humano"
  | "regla-graduada"
  | "identidad-aprendida"
  | "pendiente"
  | "humano-obsoleto"
  | "clave-ambigua";

export type Rol = "dueno" | "contador" | "cajero" | "lectura";

/** SPEC-029: RECHAZAR es «no es de este cliente» (§4.4); ANULAR, deshacer (§7). */
export type Accion = "CONFIRMAR" | "DESCARTAR" | "ETIQUETAR" | "RECHAZAR" | "ANULAR" | "LIGAR";

export type EstadoFactura = "PAGADA" | "PARCIAL" | "PENDIENTE";

/** Los estados que comunican certeza. Espejo de `core/estados.js`. */
export const ESTADOS_DE_CERTEZA: EstadoMovimiento[] = ["CONCILIADO", "IDENTIFICADO"];
export const esDeCerteza = (e: EstadoMovimiento) => ESTADOS_DE_CERTEZA.includes(e);

// ─────────────────────────── libro ───────────────────────────

/** Señales de evidencia en que se apoyó la propuesta del motor. */
export interface Senales {
  /** `NINGUNO` lo pone la propuesta que nace de una factura (SPEC-028 §9). */
  nombre?: "TOTAL" | "FUERTE" | "DEBIL" | "NULO" | "NINGUNO";
  montoExacto?: boolean;
  /** La propuesta desde factura dice el monto así, no con `montoExacto`. */
  monto?: string;
  montoEnTolerancia?: boolean;
  diferenciaCentavos?: number;
  /**
   * Lo que se esperaba cobrar: la suma de los abonos propuestos.
   *
   * `diferenciaCentavos` es absoluta y pierde el signo, así que sin esto no se
   * puede saber si el pago vino corto o largo — ni dibujar los dos lados.
   */
  esperadoCentavos?: number;
  fechaCercana?: boolean;
  facturaVerificada?: boolean;
}

export interface Sugerencia {
  numero: string;
  totalCentavos: number | null;
}

/** Una cuenta del negocio, como la guarda la configuración (SPEC-030 §4). */
export interface CuentaBancaria {
  /** Libre: puede ser un banco del que 4drando no recibe alertas (SPEC-030 §4 bis). */
  banco: string;
  tipo: "Ahorros" | "Corriente" | "Llave";
  /** Los últimos 4 dígitos, que es lo que trae la alerta. Ahorros y Corriente. */
  ultimos?: string;
  /** La llave (Bre-B): un celular, un documento, un correo o una alfanumérica. Solo Llave. */
  llave?: string;
  /** Cómo la llama el negocio. Es lo que se lee. */
  nombre: string;
}

/** De dónde salió la cuenta de un pago (SPEC-030 §5). Vacío: sin identificar. */
export type OrigenCuenta = "persona" | "banco" | "deducida" | "";

/** A qué cuenta del negocio entró un pago, ya resuelta por el servidor. */
export interface CuentaDelPago {
  banco: string;
  /** «*1592», o vacía si no se sabe. */
  cuenta: string;
  /** Vacío si el número no está en la lista del negocio. */
  nombre: string;
  /** «Ahorros», «Corriente» o «Llave»; vacío si no se sabe. */
  tipo?: string;
  origen: OrigenCuenta;
  /** «es tu única cuenta de Ahorros en Bancolombia», cuando es deducida. */
  porque: string;
  quien?: string;
  cuando?: string;
}

export interface Movimiento {
  clave: string;
  claveEstable?: boolean;
  indice?: number;
  fecha?: string | null;
  montoCentavos: number | null;
  contraparte: string;
  llave?: string;
  /**
   * Dónde ocurrió, cuando el banco lo dice. Hoy solo lo trae la consignación en
   * corresponsal, que a cambio NO dice quién pagó. Es para mostrar: no
   * identifica a nadie ni entra en el cruce.
   */
  sucursal?: string;
  /** A qué cuenta del negocio entró (SPEC-030). */
  cuentaBancaria?: CuentaDelPago | null;
  estado: EstadoMovimiento;
  origen: Origen;
  razon: string;

  /** Abonos que respaldan la propuesta. Vacío = nadie propuso nada. */
  abonosClaves?: string[];
  candidatosClaves?: string[];
  puntos?: number;
  senales?: Senales;
  factura?: string;
  /** Documento del cliente, para cruzar por llave y no por texto (SPEC-028 §4.3). */
  clienteNit?: string;
  /**
   * Las facturas que el «sí» de este pago ligaría, calculadas por el servidor
   * (SPEC-029 §4.3). Número e importe, nada más de la cartera: así el cajero,
   * que no la ve, tiene la misma protección.
   */
  sugerencias?: Sugerencia[];
  cliente?: string;

  /** Trazabilidad de la decisión humana. */
  quien?: string;
  cuando?: string;
  nota?: string;
  regla?: string;
  identificador?: string;
  decisionPrevia?: { quien: string; cuando: string };
}

export interface Resumen {
  total: number;
  resueltos: number;
  porHumano: number;
  porRegla: number;
  porAprendizaje: number;
  pendientes: number;
  /** Fracción 0–1 resuelta sin intervención humana. `null` si no hay movimientos. */
  automatizacion: number | null;
}

export interface Huerfano {
  indice?: number;
  clave: string;
  estado: EstadoHuerfano;
  razon: string;
  cliente: string;
  montoCentavos: number | null;
  /** Cuándo se registró. Es por donde se le busca el pago en el banco (SPEC-029 §13). */
  fechaPago?: string | null;
  factura?: string;
  metodo?: string;
  /** Si alguien lo dio por revisado: quién, cuándo y por qué. */
  revision?: { razon: RazonRevision; nota: string; quien: string; cuando: string };
}

export type RazonRevision = "llego-por-otro-medio" | "error-de-digitacion" | "todavia-no-llega";

export interface Factura {
  numero: string;
  /** La identidad del cliente. Es lo que se copia al etiquetar (SPEC-028 §4.2). */
  nit?: string;
  /** Cómo se le llama. Copia legible, no la identidad. */
  cliente?: string;
  estado: EstadoFactura;
  totalCentavos: number | null;
  abonadoCentavos: number;
  saldoCentavos: number;
}

/** Correos del banco que parecen pagos y no se pudieron leer (SPEC-031). */
export interface CorreosSinLeer {
  n: number;
  /** Cuántos de ellos llegaron en blanco. */
  vacios: number;
  /** Cuándo llegó el más viejo. */
  masViejo: string | null;
}

export interface Libro {
  resumen: Resumen;
  pendientes: Movimiento[];
  movimientos: Movimiento[];
  /** Abonos declarados que el banco no respalda. Solo `FALTANTE`. */
  alertas: Huerfano[];
  /** Los faltantes que alguien ya revisó (SPEC-029 §13). */
  revisadas?: Huerfano[];
  rol: Rol;
  /** Ausente para el rol `cajero`: la cartera no es asunto suyo. */
  facturas?: Factura[];
  /** Las cuentas del negocio, para elegir a mano a cuál entró un pago (SPEC-030 §6). */
  cuentasBancarias?: CuentaBancaria[];
}

// ─────────────────────────── sesión ───────────────────────────

export interface Sesion {
  rol: Rol;
  expiraEn: string;
  /** Slug de la cuenta activa. */
  cuenta?: string;
  /** Nombre legible de la cuenta. El slug es una llave, no una etiqueta. */
  cuentaNombre?: string;
  /** Quién está dentro. En un equipo, la cuenta no identifica a nadie. */
  usuario?: { nombre: string; correo: string };
}

export interface CuentaDisponible {
  slug: string;
  nombre: string;
}

/** `POST /sesion` responde 300 cuando hay más de una cuenta y no se eligió. */
export interface EligeCuenta {
  elige: string;
  cuentas: CuentaDisponible[];
}

// ─────────────────────────── operación ───────────────────────────

export interface CorreoEnRevision {
  id: string;
  message_id: string | null;
  remitente: string;
  asunto: string;
  cuerpo: string;
  motivo: string;
  recibido_en: string;
}

export type ResultadoCorreo = "REGISTRADO" | "EN_REVISION" | "RECHAZADO" | "DUPLICADO";

/** Una entrada de la bandeja de recibidos (SPEC-021). */
export interface Recibido extends CorreoEnRevision {
  resultado: ResultadoCorreo;
  /** La frase que explica el motivo. Los rechazos la traen; el resto, no. */
  detalle: string | null;
  patron: string | null;
  fuente: string | null;
  procedencia: "DIRECTA" | "REENVIADA" | null;
  movimiento_id: string | null;
  /** Cuándo se archivó, y quién. Null = está en la bandeja (SPEC-024). */
  archivado_en: string | null;
  archivado_por: string | null;
}

export interface Recibidos {
  correos: Recibido[];
  /** Cuántos rechazos se anotaron en 24 h. Con `tope`, dice si se llenó. */
  rechazados24h: number;
  /** Cuántos hay archivados. Va siempre: un filtro que no se ve no existe. */
  archivados: number;
  /** Cuántos se archivaron cada mes, los últimos doce (SPEC-024 §3 ter). */
  archivadosPorMes: { mes: string; n: number }[];
  tope: number;
}

export interface Reaccion {
  avisados: number;
  decididos: number;
  sinDecidir: number;
  medianaMinutos: number | null;
  p90Minutos: number | null;
  lectura: string;
}

export interface Metricas {
  reaccion: Reaccion;
  dias: number;
}

export interface Invitacion {
  id: string;
  token: string;
  correo: string;
  rol: Rol;
  expiraEn: string;
}

// ─────────────────────────── configuración (SPEC-017) ───────────────────────────

export type TipoFuente = "banco" | "facturacion";

export interface Fuente {
  nombre: string;
  tipo: TipoFuente;
  remitentes: string[];
}

/** El nombre de la clave tal como la guarda el backend: RECAUDA_*. */
export type ClaveConfig = string;

export interface ClaveDescrita {
  nombre: string;
  clave: ClaveConfig;
  definida: boolean;
  /** Los secretos llegan como "•••••", nunca su valor (SPEC-017 §8). */
  valor: string;
  /** Exige `mover_autonomia`, o sea el dueño. */
  autonomia: boolean;
}

export interface Configuracion {
  claves: ClaveDescrita[];
  /** Consecuencias del estado actual. No son errores: son lo que hay que leer. */
  avisos: string[];
  puedeAutonomia: boolean;
  /** A dónde se reenvían las alertas del banco (SPEC-029 §15). */
  direccionEntrada?: string | null;
  /** Los bancos de los que puede ser una cuenta (SPEC-030 §4). */
  bancos?: string[];
  /** Números que trajeron las alertas y no están en la lista, con cuántos pagos. */
  cuentasPorRegistrar?: { banco: string; ultimos: string; pagos: number }[];
}

/** Cambios parciales: `null` borra la clave (SPEC-017 §8). */
export type Cambios = Partial<Record<ClaveConfig, string | null>>;

export interface ResultadoConfig {
  ok?: boolean;
  simulado?: boolean;
  tocadas: ClaveConfig[];
  avisos: string[];
}

// ─────────────────────────── Telegram (SPEC-023) ───────────────────────────

export interface EstadoTelegram {
  bot: string;
  /** Si este servidor tiene bot y token. Sin eso no se puede ofrecer conectar. */
  disponible: boolean;
  vinculo: { chat: string; tipo: string | null; desde: string } | null;
}

export interface EnlaceTelegram {
  enlace: string;
  expiraEn: string;
  minutos: number;
}

// ─────────────────────── credenciales de servicio (SPEC-019) ───────────────────────

export interface Credencial {
  id: string;
  /** La mitad pública. Es como se nombra una credencial sin enseñar el secreto. */
  prefijo: string;
  nombre: string;
  creada_en: string;
  creada_por: string | null;
  /** `null` si nunca se ha usado. Es el dato que delata una credencial olvidada. */
  ultimo_uso: string | null;
  revocada_en: string | null;
}

/** La respuesta al crearla. `token` no vuelve a estar disponible nunca más. */
export interface CredencialCreada extends Credencial {
  token: string;
  aviso: string;
}

// ─────────────────────────── equipo (SPEC-018) ───────────────────────────

export interface Miembro {
  id: string;
  correo: string;
  nombre: string;
  rol: Rol;
  desde: string;
  /** `null` si nunca ha entrado: la invitación se aceptó y no volvió. */
  ultimo_acceso: string | null;
}

export interface InvitacionViva {
  id: string;
  correo: string;
  rol: Rol;
  tipo: "invitacion" | "restablecimiento";
  creada_en: string;
  expira_en: string;
}

export interface Equipo {
  /** Cuál de los miembros eres tú, sin tener que adivinarlo por el correo. */
  yo: string;
  /** Si además puedes cambiar algo. Lo decide el backend, no el rol que veas. */
  puedeGestionar: boolean;
  miembros: Miembro[];
  invitaciones: InvitacionViva[];
}

// ─────────────── cartera: lo que se registra a mano (SPEC-000 §2) ───────────────

/**
 * Un abono es una **afirmación humana**: alguien dijo "este pago es de tal
 * cliente". Puede estar mal, y por eso se puede borrar — a diferencia de un
 * movimiento, que es un hecho del banco.
 */
export interface Abono {
  id: number;
  fechaPago: string | null;
  cliente: string;
  nit: string;
  empresa: string;
  factura: string;
  montoCentavos: number | null;
  metodo: string;
  novedad: string;
  codigo: string;
  creadoEn?: string;
}

/** Una factura es un hecho de la DIAN. Se identifica por su número. */
export interface FacturaCartera {
  id: number;
  numero: string;
  nit: string;
  totalCentavos: number | null;
  emision: string | null;
  vence: string | null;
  cufe: string | null;
}

/** Descripción de un campo, servida por el backend desde el mismo esquema que valida. */
export interface CampoEntidad {
  campo: string;
  obligatorio: boolean;
  tipo: "texto" | "dinero" | "fecha";
  etiqueta: string;
}

export interface Campos {
  abonos: CampoEntidad[];
  facturas: CampoEntidad[];
}

/** Lo que devuelve la API cuando los datos no cuadran (422). */
export interface ProblemaCampo {
  campo: string;
  codigo: string;
  detalle: string;
}

/**
 * Una fila que el sistema del cliente mandó y no se pudo guardar
 * (SPEC-019 §8 bis).
 *
 * Lleva la fila TAL COMO LLEGÓ a propósito: sin ella el dueño ve que algo falló
 * y no puede enseñarle a su proveedor qué mandó mal — y quien nota el problema
 * no es quien escribió la integración.
 */
export interface CargaRechazada {
  id: number;
  entidad: "Abonos" | "Facturas";
  /** Prefijo de la credencial. Nunca el secreto. */
  credencial: string | null;
  fila: Record<string, unknown>;
  problemas: ProblemaCampo[];
  recibido_en: string;
}

export interface CargasRechazadas {
  rechazos: CargaRechazada[];
}

// ─────────────────────────── conexiones ───────────────────────────
//
// Qué hay conectado a la cuenta y cómo va cada cosa (SPEC-026).

export type EstadoConexion =
  | "SIN_ESTRENAR"
  | "AL_DIA"
  | "AL_DIA_CON_COLA"
  | "EN_SILENCIO"
  | "REVOCADA";

export interface Conexion {
  prefijo: string;
  nombre: string;
  /**
   * Lo que el software DIJO ser: `recauda-connect-php/0.1.0 avanpos/1.14.0`.
   * Sin verificar — sirve para ver una integración desactualizada, nunca para
   * decidir nada, y la pantalla tiene que decirlo.
   */
  agente: string;
  creadaEn: string | null;
  creadaPor: string | null;
  revocadaEn: string | null;
  /** Cualquier llamada. Un ERP que solo sube facturas tiene esto y no lecturas. */
  ultimoUso: string | null;
  /** Solo lecturas de eventos. */
  ultimaLectura: string | null;
  ultimoEventoLeido: number;
  sinLeer: number;
  horasSinLeer: number | null;
  estado: EstadoConexion;
  rechazos: number;
  ultimoRechazo: string | null;
}

export interface Integraciones {
  conexiones: Conexion[];
  resumen: {
    estado: "SIN_INTEGRAR" | "AL_DIA" | "EN_SILENCIO";
    sinLeer: number;
    horasSinLeer: number | null;
    credencial: string | null;
  };
  horasDeSilencio: number;
  rechazosSinAtribuir: number;
}
